import Rails from '@rails/ujs';
import consumer from '../../channels/consumer';

/**
 * Shared chat panel controller for both extranet and backoffice task show pages.
 *
 * Options:
 *   tabs: false         — simple single-feed mode (extranet)
 *   tabs: true          — two-feed mode with visible/internal tabs (backoffice)
 *   visibilityCallback  — fn(checked, itemType, attachmentId, url) for backoffice visibility toggles
 */
export default class ChatPanel {

  constructor(options = {}) {
    this.tabs = options.tabs || false;
    this.visibilityCallback = options.visibilityCallback || null;

    this.togglePanel    = this.togglePanel.bind(this);
    this.collapsePanel  = this.collapsePanel.bind(this);
    this.toggleExpanded = this.toggleExpanded.bind(this);
    this.autoResize     = this.autoResize.bind(this);
    this.onFileSelected = this.onFileSelected.bind(this);
    this.onFileRemove   = this.onFileRemove.bind(this);
    this.submitMessage  = this.submitMessage.bind(this);

    this.bindEvents();
    this.scrollToBottom();
  }

  bindEvents() {
    $('#chat-open-btn').on('click', this.togglePanel);
    $('#chat-close-btn').on('click', this.collapsePanel);
    $('#chat-expand-btn').on('click', this.toggleExpanded);

    $(document).on('submit', '#chat-compose-form', this.submitMessage);
    $(document).on('input', '#chat-compose-input', this.autoResize);

    $('#chat-attach-trigger').on('click', () => $('#chat-file-input').trigger('click'));
    $('#chat-file-input').on('change', this.onFileSelected);
    $('#chat-attach-remove').on('click', this.onFileRemove);

    // Always bind tab events (both backoffice 3-tab and extranet 2-tab)
    this.bindTabEvents();
    this.bindStudyCollapseEvents();
    this.initResizeHandle();
    this.bindMoeCustomerWrite();
    this.initDragAndDrop();
    this.initCable();

    if (this.visibilityCallback) {
      $(document).on('change', '.comment-visibility-checkbox', (e) => {
        const $cb = $(e.target);
        this.visibilityCallback(
          $cb.is(':checked'),
          $cb.data('item-type'),
          $cb.data('attachment-id'),
          $cb.data('url')
        );
      });
    }
  }

  // ── Study request collapsible ───────────────────────────────

  bindStudyCollapseEvents() {
    $(document).on('click', '.chat-study-toggle', (e) => {
      const $btn  = $(e.currentTarget);
      const target = $btn.data('target');
      const $body = $('#' + target);

      $btn.toggleClass('open');
      $body.toggleClass('open');
    });
  }

  // ── Tab switching (backoffice & extranet) ───────────────────

  bindTabEvents() {
    $(document).on('click', '.chat-tab-btn', (e) => {
      const $btn = $(e.currentTarget);
      const target = $btn.data('target');
      const visibility = $btn.data('visibility');

      // Toggle active tab button
      $('.chat-tab-btn').removeClass('active');
      $btn.addClass('active');

      // Toggle active feed pane
      $('.chat-feed').removeClass('active');
      $('#' + target).addClass('active');

      // Update hidden visibility fields
      $('#chat-visibility-hidden').val(visibility);

      // Clear the moe_customer compose mode badge on any tab switch
      $('#chat-compose-mode-badge').addClass('d-none');

      // Update legacy visible param for backoffice
      const isVisible = visibility === 'customer' ? 'true' : 'false';
      $('#chat-visible-param').val(isVisible);

      // Update widget border color
      const $panel = $btn.closest('.chat-panel-wrap');
      $panel.removeClass('chat-panel--border-info chat-panel--border-success chat-panel--border-warning');
      const borderClass = visibility === 'internal' ? 'chat-panel--border-success'
                        : visibility === 'moe'      ? 'chat-panel--border-warning'
                        :                             'chat-panel--border-info';
      $panel.addClass(borderClass);

      this.scrollToBottom();
    });
  }
  // ── MOE customer channel write toggle (root admin) ────────

  bindMoeCustomerWrite() {
    $(document).on('click', '[data-set-visibility]', (e) => {
      e.stopPropagation();
      const vis = $(e.currentTarget).data('set-visibility');
      $('#chat-visibility-hidden').val(vis);
      $('#chat-compose-mode-badge').removeClass('d-none');
      $('#chat-compose-input').focus();
    });

    $('#chat-compose-mode-cancel').on('click', () => {
      const tabVis = $('.chat-tab-btn.active').data('visibility') || 'moe';
      $('#chat-visibility-hidden').val(tabVis);
      $('#chat-compose-mode-badge').addClass('d-none');
    });
  }
  // ── Panel collapse / expand ────────────────────────────────

  togglePanel() {
    const layout = $('#show-layout');
    if (layout.hasClass('chat-layout-collapsed')) {
      layout.removeClass('chat-layout-collapsed');
      this.scrollToBottom();
    } else {
      layout.addClass('chat-layout-collapsed');
    }
  }

  collapsePanel() {
    $('#show-layout').addClass('chat-layout-collapsed');
  }

  // ── Half-screen expand (col-6) ─────────────────────────────

  toggleExpanded() {
    const $layout  = $('#show-layout');
    const $icon    = $('#chat-expand-btn i');
    const expanding = !$layout.hasClass('chat-layout-expanded');

    $layout.toggleClass('chat-layout-expanded');
    $icon
      .toggleClass('fa-expand-alt',   !expanding)
      .toggleClass('fa-compress-alt',  expanding);
    $('#chat-expand-btn').attr('title', expanding ? 'Réduire' : 'Agrandir');
    this.scrollToBottom();
  }

  // ── Horizontal drag resize (desktop only) ─────────────────

  initResizeHandle() {
    if (window.innerWidth <= 768) return;

    const $chatCol = $('#show-chat-col');
    const $handle  = $('#chat-resize-handle');
    if (!$handle.length || !$chatCol.length) return;

    const MIN_WIDTH = 340;

    $handle.on('mousedown', (e) => {
      // Disable resize while in expanded (col-6) mode
      if ($('#show-layout').hasClass('chat-layout-expanded')) return;

      const startX     = e.clientX;
      const startWidth = $chatCol.outerWidth();

      $handle.addClass('is-dragging');
      $('body').addClass('chat-resizing');

      $(document).on('mousemove.chatResize', (ev) => {
        const delta    = startX - ev.clientX;
        const maxWidth = $('#show-layout').outerWidth() * 0.65;
        const newWidth = Math.min(maxWidth, Math.max(MIN_WIDTH, startWidth + delta));
        $chatCol[0].style.setProperty('--chat-col-width', newWidth + 'px');
      });

      $(document).on('mouseup.chatResize', () => {
        $handle.removeClass('is-dragging');
        $('body').removeClass('chat-resizing');
        $(document).off('mousemove.chatResize mouseup.chatResize');
      });

      e.preventDefault();
    });
  }

  // ── AJAX message submit ────────────────────────────────────

  submitMessage(e) {
    e.preventDefault();

    const form     = document.getElementById('chat-compose-form');
    const formData = new FormData(form);
    const $btn     = $(form).find('.chat-send-btn');

    $btn.prop('disabled', true);

    Rails.ajax({
      type:    'POST',
      url:     form.action,
      data:    formData,
      success: (response) => {
        this.handleMessageSuccess(response, form);
        $btn.prop('disabled', false);
      },
      error: (response) => {
        this.handleMessageError(response);
        $btn.prop('disabled', false);
      }
    });
  }

  handleMessageSuccess(response, form) {
    // Determine which feed to append to via active tab
    let feed;
    const activeTab = $('.chat-tab-btn.active');
    if (activeTab.length) {
      const targetId = activeTab.data('target');
      feed = document.getElementById(targetId);
    } else {
      feed = document.querySelector('.chat-feed.active');
    }

    if (!feed) return;

    // Remove empty state placeholder on first message
    const empty = feed.querySelector('.chat-empty-state');
    if (empty) empty.remove();

    let cableAlreadyCounted = false;

    if (response.item_html) {
      // The cable broadcast may have already inserted a 'theirs' bubble before this
      // AJAX response arrived (race condition). Replace it with the correct 'mine' version.
      const tmp = document.createElement('div');
      tmp.innerHTML = response.item_html;
      const newEl = tmp.firstElementChild;
      const msgId = newEl && newEl.dataset.messageId;

      if (msgId) {
        const existing = feed.querySelector(`[data-message-id="${msgId}"]`);
        if (existing) {
          // Cable already inserted this element (and already incremented the tab count)
          cableAlreadyCounted = true;
          existing.outerHTML = response.item_html;
        } else {
          feed.insertAdjacentHTML('beforeend', response.item_html);
        }
      } else {
        feed.insertAdjacentHTML('beforeend', response.item_html);
      }
    }

    // Keep moe-customer section pinned at bottom of moe feed
    if (feed.id === 'chat-feed-moe') {
      const moeSection = feed.querySelector('.chat-moe-customer-section');
      if (moeSection) {
        const prevSibling = moeSection.previousElementSibling;
        if (prevSibling && prevSibling.classList.contains('chat-study-divider')) {
          feed.appendChild(prevSibling);
        }
        feed.appendChild(moeSection);
      }
    }

    // Only increment counters if the cable path didn't already do it
    if (!cableAlreadyCounted) {
      this.updateCounts(1);
    }

    // Reset form
    form.reset();
    this.onFileRemove();
    const input = document.getElementById('chat-compose-input');
    if (input) input.style.height = 'auto';

    this.scrollToBottom();
  }

  handleMessageError(response) {
    const msg = (response && response.error)
      ? response.error
      : "Une erreur est survenue. Veuillez réessayer.";

    if (typeof $.toast === 'function') {
      $.toast({
        text:     msg,
        icon:     'error',
        position: 'top-right',
        hideAfter: 4000
      });
    } else {
      alert(msg);
    }
  }

  updateCounts(delta) {
    // Header total count
    const $headerCount = $('.chat-header-count');
    if ($headerCount.length) {
      const current = parseInt($headerCount.text(), 10) || 0;
      $headerCount.text(current + delta);
    }

    // Tab count — always update regardless of mode (fix 4)
    const $activeBtn = $('.chat-tab-btn.active');
    if ($activeBtn.length) {
      const text = $activeBtn.text();
      const match = text.match(/\((\d+)\)/);
      if (match) {
        const newCount = parseInt(match[1], 10) + delta;
        $activeBtn.html($activeBtn.html().replace(/\(\d+\)/, `(${newCount})`));
      }
    }

    // Open button badge
    const $badge = $('#chat-open-btn .badge');
    if ($badge.length) {
      const current = parseInt($badge.text(), 10) || 0;
      $badge.text(current + delta);
    }
  }

  // ── Scroll to latest message ───────────────────────────────

  scrollToBottom() {
    const activeFeed = document.querySelector('.chat-feed.active');
    if (activeFeed) activeFeed.scrollTop = activeFeed.scrollHeight;
  }

  isScrolledToBottom(feed) {
    if (!feed) return true;
    return feed.scrollHeight - feed.scrollTop - feed.clientHeight < 50;
  }

  // ── Textarea auto-resize ───────────────────────────────────

  autoResize(e) {
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 100)}px`;
  }

  // ── File attachment ────────────────────────────────────────

  onFileSelected(e) {
    const file = e.target.files[0];
    if (!file) return;

    const name = file.name;

    $('#chat-attach-filename').text(name);
    $('#chat-attach-preview').removeClass('d-none');
    $('#chat-attach-trigger').addClass('has-file');
  }

  onFileRemove() {
    $('#chat-file-input').val('');
    $('#chat-attach-filename').text('');
    $('#chat-attach-preview').addClass('d-none');
    $('#chat-attach-trigger').removeClass('has-file');
  }

  // ── Drag-and-drop file attachment ─────────────────────────

  initDragAndDrop() {
    const panel = document.getElementById('chat-panel');
    if (!panel) return;

    let dragCounter = 0;

    panel.addEventListener('dragenter', (e) => {
      if (!Array.from(e.dataTransfer.types).includes('Files')) return;
      e.preventDefault();
      dragCounter++;
      panel.classList.add('chat-panel--drag-over');
    });

    panel.addEventListener('dragover', (e) => {
      if (!Array.from(e.dataTransfer.types).includes('Files')) return;
      e.preventDefault();
    });

    const clearDrag = () => {
      dragCounter = 0;
      panel.classList.remove('chat-panel--drag-over');
    };

    panel.addEventListener('dragleave', (e) => {
      dragCounter--;
      // relatedTarget is null when leaving the viewport entirely
      if (dragCounter <= 0 || !e.relatedTarget || !panel.contains(e.relatedTarget)) {
        clearDrag();
      }
    });

    document.addEventListener('dragleave', (e) => {
      if (!e.relatedTarget) clearDrag();
    });

    document.addEventListener('dragend', clearDrag);

    panel.addEventListener('drop', (e) => {
      e.preventDefault();
      clearDrag();
      const files = e.dataTransfer.files;
      if (!files.length) return;

      const fileInput = document.getElementById('chat-file-input');
      if (!fileInput) return;

      const dt = new DataTransfer();
      dt.items.add(files[0]);
      fileInput.files = dt.files;
      this.onFileSelected({ target: fileInput });

      const textarea = document.getElementById('chat-compose-input');
      if (textarea) textarea.focus();
    });
  }

  // ── ActionCable real-time connection ──────────────────────

  initCable() {
    const panel = document.getElementById('chat-panel');
    if (!panel) return;

    const taskId = panel.dataset.taskId;
    if (!taskId) return;

    this.portalContext    = panel.dataset.portalContext || 'extranet';

    this.cableSubscription = consumer.subscriptions.create(
      { channel: 'TaskChatChannel', task_id: taskId },
      {
        received:  (data) => this.handleCableData(data),
        rejected:  ()     => {},
        connected: ()     => {}
      }
    );
  }

  handleCableData(data) {
    switch (data.type) {
      case 'new_message': return this.handleCableNewMessage(data);
      case 'delete':      return this.handleCableDelete(data);
    }
  }

  handleCableNewMessage({ visibility, message_id, item_html }) {
    const feedId = this.feedIdForVisibility(visibility);
    if (!feedId) return;

    const feed = document.getElementById(feedId);
    if (!feed) return;

    // If this sender already inserted the element via AJAX, skip entirely
    // (avoids double-counting and duplicate rendering)
    if (feed.querySelector(`[data-message-id="${message_id}"]`)) return;

    const empty = feed.querySelector('.chat-empty-state');
    if (empty) empty.remove();

    const moeSection = feed.querySelector('.chat-moe-customer-section');
    if (moeSection) {
      moeSection.insertAdjacentHTML('beforebegin', item_html);
    } else {
      feed.insertAdjacentHTML('beforeend', item_html);
    }

    // Only increment the tab count for genuinely incoming messages
    const tabBtn = document.querySelector(`.chat-tab-btn[data-target="${feedId}"]`);
    if (tabBtn) {
      tabBtn.innerHTML = tabBtn.innerHTML.replace(/\((\d+)\)/, (_, n) => `(${parseInt(n, 10) + 1})`);
    }

    this.scrollToBottom();
  }

  feedIdForVisibility(visibility) {
    const ctx = this.portalContext;

    if (ctx === 'extranet') {
      if (visibility === 'customer')     return 'chat-feed-customer';
      if (visibility === 'moe_customer') return 'chat-feed-moe';
      return null;
    }

    if (ctx === 'moe') {
      if (visibility === 'moe')          return 'chat-feed-internal';
      if (visibility === 'moe_customer') return 'chat-feed-customer';
      return null;
    }

    // backoffice: all four feeds
    if (visibility === 'moe_customer') return 'chat-feed-moe';
    return `chat-feed-${visibility}`;
  }

  handleCableDelete({ message_id }) {
    const el = document.querySelector(`[data-message-id="${message_id}"]`);
    if (!el) return;

    const feedId = el.closest('.chat-feed')?.id;
    el.remove();

    if (feedId) {
      const tabBtn = document.querySelector(`.chat-tab-btn[data-target="${feedId}"]`);
      if (tabBtn) {
        tabBtn.innerHTML = tabBtn.innerHTML.replace(/\((\d+)\)/, (_, n) => `(${Math.max(0, parseInt(n, 10) - 1)})`);
      }
    }
  }

  onDestroy() {
    $('#chat-open-btn').off('click', this.togglePanel);
    $('#chat-close-btn').off('click', this.collapsePanel);
    $(document).off('submit', '#chat-compose-form', this.submitMessage);
    $(document).off('input', '#chat-compose-input', this.autoResize);
    $(document).off('click', '.chat-tab-btn');
    $(document).off('change', '.comment-visibility-checkbox');
    $('#chat-attach-trigger').off('click');
    $('#chat-file-input').off('change', this.onFileSelected);
    $('#chat-attach-remove').off('click', this.onFileRemove);
    if (this.cableSubscription) this.cableSubscription.unsubscribe();
  }
}
